import React, { useEffect, useState, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams, RowDoubleClickedEvent } from 'ag-grid-community';
import ReactionViewer from './ReactionViewer';
import { ReactionEditorModal } from './ReactionEditor';
import { chemistryApi } from '../../api/chemistry';
import StructureImage from './StructureImage';

interface ReactionParticipant {
  id: number;
  role: string;
  smiles?: string | null;
  inchi_key?: string | null;
}

interface Reaction {
  id: number;
  file_id: number;
  rxn_index: number;
  reaction_smiles: string;
  rxnfile?: string;
  properties: Record<string, any>;
  created_at: string;
  participants?: ReactionParticipant[];
}

interface LinkedBiologic {
  id: number;
  biologic_id: number;
  role: string;
  name: string | null;
  uniprot_id: string | null;
  sequence_type: string | null;
}

interface ReactionGridProps {
  fileId: number;
  onSearchStructure?: (smiles: string) => void;
  onNavigateToBiologic?: (biologicId: number) => void;
}

// Clickable participant chip component
const ParticipantChip: React.FC<{
  smiles: string;
  onClick?: (smiles: string) => void;
}> = ({ smiles, onClick }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(smiles);
    }}
    onDoubleClick={(e) => e.stopPropagation()}
    className="inline-flex items-center justify-center m-0.5 bg-gray-100 hover:bg-teal-100
               border border-gray-300 hover:border-teal-400 rounded cursor-pointer
               transition-colors"
    style={{ width: 76, height: 64, padding: 2 }}
    title={`Click to search: ${smiles}`}
  >
    <StructureImage smiles={smiles} size={64} />
  </button>
);

// Cell renderer for participants (reactants or products) — reads reaction_participants
const ParticipantsCellRenderer: React.FC<ICellRendererParams & {
  role: 'reactant' | 'product';
  onSearch?: (smiles: string) => void;
}> = (params) => {
  const participants: ReactionParticipant[] = params.data?.participants || [];
  const items = participants.filter((p) => p.role === params.role && p.smiles);

  if (items.length === 0) return <span className="text-gray-400">-</span>;

  return (
    <div className="flex flex-wrap py-1">
      {items.map((p) => (
        <ParticipantChip key={p.id} smiles={p.smiles as string} onClick={params.onSearch} />
      ))}
    </div>
  );
};

const ReactionCellRenderer: React.FC<ICellRendererParams> = (params) => {
  if (!params.data?.id) return null;
  return (
    <div className="py-1">
      {/* Render wide (natural aspect) and let ReactionViewer scale it to the cell. */}
      <ReactionViewer reactionId={params.data.id} width={600} height={118} />
    </div>
  );
};

// Catalyzed By renderer - chemical agents (catalyst/reagent) + linked enzymes
const CatalyzedByRenderer: React.FC<ICellRendererParams & {
  linkedBiologics: Record<number, LinkedBiologic[]>;
  onNavigate?: (biologicId: number) => void;
  onSearch?: (smiles: string) => void;
}> = (params) => {
  const reactionId = params.data?.id;
  const biologics = params.linkedBiologics?.[reactionId] || [];

  // Enzyme catalysts (linked biologics)
  const enzymes = biologics.filter(b => b.role === 'catalyst');
  // Chemical agents from reaction_participants (the middle ">agent>" species)
  const participants: ReactionParticipant[] = params.data?.participants || [];
  const agents = participants.filter(p => (p.role === 'catalyst' || p.role === 'reagent') && p.smiles);

  if (enzymes.length === 0 && agents.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="flex flex-wrap py-1 gap-1 items-center">
      {agents.map((p) => (
        <ParticipantChip key={`agent-${p.id}`} smiles={p.smiles as string} onClick={params.onSearch} />
      ))}
      {enzymes.map((biologic) => (
        <button
          key={biologic.id}
          onClick={(e) => {
            e.stopPropagation();
            params.onNavigate?.(biologic.biologic_id);
          }}
          className="px-2 py-0.5 text-xs bg-green-100 text-green-800 hover:bg-green-200
                     border border-green-300 rounded cursor-pointer transition-colors"
          title={`${biologic.name || 'Enzyme'} (${biologic.uniprot_id || 'No UniProt'}) - Click to view`}
        >
          {biologic.name || biologic.uniprot_id || `Enzyme #${biologic.biologic_id}`}
        </button>
      ))}
    </div>
  );
};

export const ReactionGrid: React.FC<ReactionGridProps> = ({ fileId, onSearchStructure, onNavigateToBiologic }) => {
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedReaction, setSelectedReaction] = useState<Reaction | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loadingReaction, setLoadingReaction] = useState(false);
  const [linkedBiologics, setLinkedBiologics] = useState<Record<number, LinkedBiologic[]>>({});

  const loadLinkedBiologics = useCallback(async (reactionList: Reaction[]) => {
    const biologicsMap: Record<number, LinkedBiologic[]> = {};
    await Promise.all(
      reactionList.map(async (reaction) => {
        try {
          const biologics = await chemistryApi.getReactionBiologics(reaction.id);
          biologicsMap[reaction.id] = biologics;
        } catch {
          biologicsMap[reaction.id] = [];
        }
      })
    );
    setLinkedBiologics(biologicsMap);
  }, []);

  useEffect(() => {
    const fetchReactions = async () => {
      setLoading(true);
      try {
        const response = await chemistryApi.getReactions({ fileId });
        setReactions(response.reactions);
        setTotalCount(response.total);
        // Load linked biologics in background
        loadLinkedBiologics(response.reactions);
      } catch (err) {
        console.error('Error loading reactions:', err);
      } finally {
        setLoading(false);
      }
    };

    if (fileId) {
      fetchReactions();
    }
  }, [fileId, loadLinkedBiologics]);

  const openReactionEditor = useCallback(async (reaction: Reaction) => {
    if (!reaction?.id) return;

    setLoadingReaction(true);
    try {
      // The newer reaction-detail route nests the chemical payload under
      // `reaction`, while the original route returned a flat object. Support
      // both so the editor always receives a real structure and row metadata.
      const detail: any = await chemistryApi.getReaction(reaction.id);
      const normalizedDetail: Reaction = detail?.reaction
        ? {
            ...reaction,
            id: detail.id ?? reaction.id,
            rxnfile: detail.reaction.rxnfile || reaction.rxnfile,
            reaction_smiles: detail.reaction.reaction_smiles || reaction.reaction_smiles,
          }
        : { ...reaction, ...detail };
      setSelectedReaction(normalizedDetail);
      setEditorOpen(true);
    } catch (err) {
      console.error('Error fetching reaction:', err);
    } finally {
      setLoadingReaction(false);
    }
  }, []);

  const handleRowDoubleClick = useCallback(async (event: RowDoubleClickedEvent) => {
    await openReactionEditor(event.data as Reaction);
  }, [openReactionEditor]);

  const handleCloseEditor = useCallback(() => {
    setEditorOpen(false);
    setSelectedReaction(null);
  }, []);

  const handleSaveReaction = useCallback(async (rxnfile: string, smiles: string) => {
    if (!selectedReaction?.id) {
      console.error('[ReactionGrid] No reaction selected for save');
      return;
    }

    try {
      console.log('[ReactionGrid] Saving reaction:', { id: selectedReaction.id, rxnfileLen: rxnfile?.length, smiles });

      const updated = await chemistryApi.updateReaction(selectedReaction.id, {
        rxnfile,
        reaction_smiles: smiles,
      });

      // Update local state with the updated reaction
      setReactions(prev => prev.map(r =>
        r.id === updated.id
          ? { ...r, reaction_smiles: updated.reaction_smiles, rxnfile: updated.rxnfile }
          : r
      ));

      console.log('[ReactionGrid] Reaction saved successfully');
      handleCloseEditor();
    } catch (err) {
      console.error('[ReactionGrid] Failed to save reaction:', err);
    }
  }, [selectedReaction, handleCloseEditor]);

  const handleParticipantClick = useCallback((smiles: string) => {
    console.log('[ReactionGrid] Participant clicked:', smiles);
    if (onSearchStructure) {
      onSearchStructure(smiles);
    }
  }, [onSearchStructure]);

  const columnDefs: ColDef[] = [
    {
      headerName: '#',
      field: 'rxn_index',
      width: 70,
      pinned: 'left',
      cellClass: 'font-mono text-gray-600',
    },
    {
      headerName: 'Reaction',
      colId: 'reaction',
      field: 'id',
      cellRenderer: ReactionCellRenderer,
      width: 360,
      autoHeight: true,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Reactants',
      colId: 'reactants',
      cellRenderer: (params: ICellRendererParams) => (
        <ParticipantsCellRenderer {...params} role="reactant" onSearch={handleParticipantClick} />
      ),
      width: 180,
      autoHeight: true,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Products',
      colId: 'products',
      cellRenderer: (params: ICellRendererParams) => (
        <ParticipantsCellRenderer {...params} role="product" onSearch={handleParticipantClick} />
      ),
      width: 180,
      autoHeight: true,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Catalyzed By',
      colId: 'catalyzedBy',
      cellRenderer: (params: ICellRendererParams) => (
        <CatalyzedByRenderer
          {...params}
          linkedBiologics={linkedBiologics}
          onNavigate={onNavigateToBiologic}
          onSearch={handleParticipantClick}
        />
      ),
      width: 150,
      autoHeight: true,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Reaction SMILES',
      field: 'reaction_smiles',
      flex: 1,
      minWidth: 200,
      tooltipField: 'reaction_smiles',
      cellRenderer: (params: ICellRendererParams) => (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            openReactionEditor(params.data as Reaction);
          }}
          onDoubleClick={(event) => event.stopPropagation()}
          className="w-full h-full text-left font-mono text-sm text-teal-700 hover:text-teal-900 hover:underline truncate"
          title="Open this reaction in Ketcher"
        >
          {params.value || '-'}
        </button>
      ),
    },
  ];

  // Add property columns dynamically
  const propertyColumns: ColDef[] = reactions.length > 0
    ? Object.keys(reactions[0].properties || {}).map(key => ({
        headerName: key,
        valueGetter: (params: any) => params.data?.properties?.[key],
        flex: 1,
        minWidth: 120,
      }))
    : [];

  const allColumns = [...columnDefs, ...propertyColumns];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
        <span className="ml-3 text-gray-600">Loading reactions...</span>
      </div>
    );
  }

  if (reactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
        <p>No reactions found in this file</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{totalCount}</span> reaction{totalCount !== 1 ? 's' : ''} in file
          <span className="ml-4 text-gray-400">Click a Reaction SMILES or double-click a row to edit</span>
        </div>
      </div>

      {loadingReaction && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center">
            <div className="animate-spin h-5 w-5 border-2 border-teal-500 border-t-transparent rounded-full mr-3"></div>
            Loading reaction...
          </div>
        </div>
      )}

      <div className="ag-theme-alpine flex-1" style={{ minHeight: 400 }}>
        <AgGridReact
          rowData={reactions}
          columnDefs={allColumns}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          getRowId={(params) => String(params.data.id)}
          rowHeight={132}
          headerHeight={40}
          animateRows={true}
          suppressRowHoverHighlight={false}
          rowClass="hover:bg-gray-50 cursor-pointer"
          onRowDoubleClicked={handleRowDoubleClick}
        />
      </div>

      <ReactionEditorModal
        isOpen={editorOpen}
        onClose={handleCloseEditor}
        onSave={handleSaveReaction}
        initialReaction={selectedReaction?.reaction_smiles || selectedReaction?.rxnfile || ''}
        title={`Edit Reaction #${selectedReaction?.rxn_index ?? ''}`}
      />
    </div>
  );
};

export default ReactionGrid;
